import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { MeetingDetail } from '@/pages/MeetingDetail';
import { ReadOnlyMeetingDetail } from '@/pages/ReadOnlyMeetingDetail';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { getMeeting } from '@/api/meetings';

export function MeetingDetailRouter() {
  const { id } = useParams();
  // State for user and meeting information
  const [isFreeUser, setIsFreeUser] = useState<boolean | null>(null);
  const [isOwner, setIsOwner] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { subscription, checkSubscriptionStatus, lastSubscriptionChangeTime } = useAuth();
  
  // Last checked timestamp to avoid unnecessary rechecks
  const lastCheckedRef = useRef(0);
  // Add a flag to track if we've already checked subscription status
  const [hasCheckedSubscription, setHasCheckedSubscription] = useState(false);
  
  // Check subscription status and meeting ownership
  useEffect(() => {
    const determineAccess = async () => {
      // If we already have both subscription status and ownership info, skip the check
      if (!isLoading && isFreeUser !== null && isOwner !== null && hasCheckedSubscription) {
        return;
      }
      
      setIsLoading(true);
      const now = Date.now();
      
      try {
        // Only check subscription if:
        // 1. We haven't checked it yet OR
        // 2. Subscription has explicitly changed (via user action) since our last check
        const subscriptionChanged = lastSubscriptionChangeTime > lastCheckedRef.current;
        let data = subscription;
        
        if (!hasCheckedSubscription || subscriptionChanged) {
          // Only call the API if we need fresh data
          try {
            data = await checkSubscriptionStatus(subscriptionChanged);
            setHasCheckedSubscription(true);
          } catch (error) {
            console.error("Error checking subscription:", error);
            // Fall back to cached subscription data if available
            data = subscription;
          }
        }
        
        // Set free user status if we have subscription data
        if (data) {
          setIsFreeUser(data.status === 'free');
        }
        
        // If we have an ID and don't know ownership status, check meeting ownership
        if (id && isOwner === null) {
          try {
            // Fetch meeting details - the server now includes isOwner property
            const response = await getMeeting(id);
            const meeting = response.meeting;
            
            // Use the isOwner property directly from the server response
            setIsOwner(meeting.isOwner);
          } catch (error) {
            console.error("Error fetching meeting details:", error);
            // Default to not owner if there's an error
            setIsOwner(false);
          }
        }
        
        // Update the last checked timestamp
        lastCheckedRef.current = now;
      } catch (error) {
        console.error("Failed to check subscription status:", error);
        // Default to non-free to be safe (shows editable view)
        setIsFreeUser(false);
        setIsOwner(false); // Default to not owner to prevent unauthorized access
      } finally {
        setIsLoading(false);
      }
    };
    
    determineAccess();
  }, [id, subscription, checkSubscriptionStatus, lastSubscriptionChangeTime, isFreeUser, isOwner, isLoading, hasCheckedSubscription]);
  
  // Show loading spinner while determining access
  if (isLoading) {
    return <LoadingSpinner />;
  }
  
  // Determine which component to render based on subscription status and ownership
  // 1. Free users always get read-only view
  // 2. Premium users get read-only view for shared meetings
  // 3. Premium users get full edit view for meetings they own
  if (isFreeUser || (isFreeUser === false && isOwner === false)) {
    return <ReadOnlyMeetingDetail />;
  } else {
    return <MeetingDetail />;
  }
}
